import { Controller } from "@hotwired/stimulus"
import Swiper from "swiper"
import { A11y, Autoplay, Navigation, Pagination } from "swiper/modules"

const TABLET_BREAKPOINT = 768
const DESKTOP_BREAKPOINT = 1024

export default class extends Controller {
  static targets = ["track", "slide", "dots", "prev", "next"]

  static values = {
    autoWidth: { type: Boolean, default: false },
    forceSingle: { type: Boolean, default: false },
    spaceBetween: { type: Number, default: 24 },
    interval: { type: Number, default: 0 }
  }

  connect() {
    this.reinitializing = false
    this.loopResizeObserver = new ResizeObserver(() => this.refreshCarousel())
    this.loopResizeObserver.observe(this.trackTarget)
    if (this.autoWidthValue && this.slideTargets[0]) {
      this.loopResizeObserver.observe(this.slideTargets[0])
    }
    this.refreshCarousel()
  }

  disconnect() {
    cancelAnimationFrame(this.loopSyncFrame)
    this.loopResizeObserver?.disconnect()
    this.swiper?.destroy(true, true)
    this.swiper = null
  }

  initializeSwiper(initialSlide = 0) {
    const carouselState = this.carouselState
    this.slideEnabled = carouselState.slideEnabled
    this.loopEnabled = carouselState.loopEnabled
    this.swiper = new Swiper(this.trackTarget, {
      modules: [A11y, Autoplay, Navigation, Pagination],
      ...this.layoutOptions,
      a11y: {
        enabled: true,
        paginationBulletMessage: this.paginationBulletMessage,
        prevSlideMessage: this.previousSlideMessage,
        nextSlideMessage: this.nextSlideMessage
      },
      allowTouchMove: this.slideEnabled,
      autoplay: this.autoplayOptions(this.loopEnabled),
      centerInsufficientSlides: true,
      grabCursor: this.slideEnabled,
      initialSlide: Math.min(initialSlide, Math.max(0, this.slideCount - 1)),
      loop: this.loopEnabled,
      navigation: this.navigationOptions,
      pagination: this.paginationOptions,
      preventClicks: false,
      preventClicksPropagation: false,
      watchOverflow: true,
      watchSlidesProgress: true,
      on: {
        init: (swiper) => this.swiperReady(swiper),
        breakpoint: (swiper) => this.swiperLayoutChanged(swiper),
        resize: (swiper) => this.swiperLayoutChanged(swiper),
        slideChangeTransitionEnd: (swiper) => this.slideSettled(swiper)
      }
    })
  }

  get layoutOptions() {
    if (this.autoWidthValue) {
      return {
        slidesPerView: "auto",
        spaceBetween: this.spaceBetweenValue
      }
    }

    if (this.forceSingleValue) {
      return {
        slidesPerView: 1,
        spaceBetween: this.spaceBetweenValue
      }
    }

    return {
      slidesPerView: 1,
      spaceBetween: this.spaceBetweenValue,
      breakpoints: {
        [TABLET_BREAKPOINT]: { slidesPerView: 2 },
        [DESKTOP_BREAKPOINT]: { slidesPerView: 3 }
      }
    }
  }

  get navigationOptions() {
    if (!this.hasPrevTarget && !this.hasNextTarget) return false

    return {
      addIcons: false,
      prevEl: this.hasPrevTarget ? this.prevTarget : null,
      nextEl: this.hasNextTarget ? this.nextTarget : null
    }
  }

  get paginationOptions() {
    if (!this.hasDotsTarget) return false

    return {
      clickable: true,
      el: this.dotsTarget
    }
  }

  get paginationBulletMessage() {
    return "Go to slide {{index}}"
  }

  get previousSlideMessage() {
    if (!this.hasPrevTarget) return "Previous slide"
    return this.prevTarget.getAttribute("aria-label") || "Previous slide"
  }

  get nextSlideMessage() {
    if (!this.hasNextTarget) return "Next slide"
    return this.nextTarget.getAttribute("aria-label") || "Next slide"
  }

  get slideCount() {
    return this.slideTargets.length
  }

  get carouselState() {
    if (this.autoWidthValue) {
      const slideWidth = this.slideTargets[0]?.offsetWidth || 0
      const stepWidth = slideWidth + this.spaceBetweenValue
      if (this.slideCount === 0 || slideWidth <= 0 || stepWidth <= 0) {
        return { slideEnabled: false, loopEnabled: false }
      }

      const containerWidth = this.trackTarget.clientWidth
      const fullyVisibleSlides = Math.max(
        1,
        Math.floor((containerWidth + this.spaceBetweenValue) / stepWidth)
      )
      const swiperVisibleSlides = Math.max(1, Math.ceil(containerWidth / stepWidth))

      return {
        slideEnabled: this.slideCount > fullyVisibleSlides,
        loopEnabled: this.slideCount > swiperVisibleSlides
      }
    }

    let slidesPerView = 1
    if (!this.forceSingleValue && window.innerWidth >= DESKTOP_BREAKPOINT) {
      slidesPerView = 3
    } else if (!this.forceSingleValue && window.innerWidth >= TABLET_BREAKPOINT) {
      slidesPerView = 2
    }
    const enabled = this.slideCount > slidesPerView
    return { slideEnabled: enabled, loopEnabled: enabled }
  }

  refreshCarousel() {
    if (!this.swiper) {
      if (this.trackTarget.clientWidth > 0) this.initializeSwiper()
      return
    }

    this.scheduleLoopSync()
  }

  swiperLayoutChanged(swiper) {
    this.scheduleLoopSync()
    this.layoutChanged(swiper)
  }

  scheduleLoopSync() {
    cancelAnimationFrame(this.loopSyncFrame)
    this.loopSyncFrame = requestAnimationFrame(() => {
      this.loopSyncFrame = null
      this.syncLoopMode()
    })
  }

  syncLoopMode() {
    if (!this.swiper || this.swiper.destroyed || this.reinitializing) return
    const carouselState = this.carouselState
    if (
      carouselState.loopEnabled === this.loopEnabled &&
      carouselState.slideEnabled === this.slideEnabled
    ) return

    this.reinitializing = true
    const initialSlide = this.swiper.realIndex
    this.swiper.destroy(true, true)
    this.initializeSwiper(initialSlide)
    this.reinitializing = false
  }

  autoplayOptions(loopEnabled) {
    if (!loopEnabled || this.intervalValue <= 0) return false

    return {
      delay: this.intervalValue,
      disableOnInteraction: false,
      pauseOnMouseEnter: true
    }
  }

  swiperReady() {}

  slideSettled() {}

  layoutChanged() {}
}
